import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../../../../axiosConfig';
import { Plus, Download, FileText, Edit2, Trash2 } from 'lucide-react';

// ────────────────────────────────────────────────
// Main List Component
// ────────────────────────────────────────────────
const CustomPageList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    name: '',
    isActive: ''
  });
  const [pageList, setPageList] = useState([]);
  const [filterLoading, setFilterLoading] = useState(false);
  const [totalItemsAll, setTotalItemsAll] = useState(0);
  const [error, setError] = useState(null);

  const pageSizeOptions = [5, 10, 20, 50];

  const [debouncedFilters, setDebouncedFilters] = useState(filters);

  useEffect(() => {
    const startFromUrl = parseInt(searchParams.get('start'), 10);
    const lengthFromUrl = parseInt(searchParams.get('length'), 10);
    const nameFromUrl = searchParams.get('name') || '';
    const isActiveFromUrl = searchParams.get('isActive') || '';

    const length = pageSizeOptions.includes(lengthFromUrl) ? lengthFromUrl : 10;
    const start = startFromUrl >= 0 ? startFromUrl : 0;
    const page = Math.floor(start / length) + 1;

    setCurrentPage(page);
    setItemsPerPage(length);
    const newFilters = {
      name: nameFromUrl,
      isActive: isActiveFromUrl
    };
    setFilters(newFilters);
    setDebouncedFilters(newFilters);
  }, [searchParams]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilters(filters);
    }, 300);
    return () => clearTimeout(timer);
  }, [filters]);

  const updateUrlParams = useCallback((page, size, currentFilters = debouncedFilters) => {
    const params = new URLSearchParams();
    const start = Math.max(0, (page - 1) * size);

    params.set('start', start.toString());
    params.set('length', size.toString());

    if (currentFilters.name) params.set('name', currentFilters.name);
    if (currentFilters.isActive) params.set('isActive', currentFilters.isActive);

    setSearchParams(params, { replace: true });
  }, [setSearchParams]);

  const normalizedNameFilter = debouncedFilters.name.trim().toLowerCase();
  const activeFilter = debouncedFilters.isActive;
  const filteredPages = pageList.filter((page) => {
    const pageName = String(page.name || page.pageMeta || '').toLowerCase();
    const matchesName = !normalizedNameFilter || pageName.includes(normalizedNameFilter);
    const matchesStatus = activeFilter === '' || page.isActive === (activeFilter === 'true');
    return matchesName && matchesStatus;
  });

  const totalItems = filteredPages.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const pagedPages = filteredPages.slice(startIndex, endIndex);
  const shouldShowLandingRow = currentPage === 1 && normalizedNameFilter === '' && (activeFilter === '' || activeFilter === 'true');

  const handlePageChange = useCallback((page) => {
    const nextTotalPages = Math.ceil(filteredPages.length / itemsPerPage) || 1;
    if (page >= 1 && page <= nextTotalPages) {
      setCurrentPage(page);
      updateUrlParams(page, itemsPerPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [filteredPages.length, itemsPerPage, updateUrlParams]);

  const handlePageSizeChange = useCallback((size) => {
    if (pageSizeOptions.includes(size)) {
      setCurrentPage(1);
      setItemsPerPage(size);
      updateUrlParams(1, size);
    }
  }, [updateUrlParams]);

  const handleFilterChange = useCallback((filterName, value) => {
    const newFilters = { ...filters, [filterName]: value };
    setFilters(newFilters);

    if (filterName !== 'name') {
      setCurrentPage(1);
      setDebouncedFilters(newFilters);
      updateUrlParams(1, itemsPerPage, newFilters);
    }
  }, [filters, itemsPerPage, updateUrlParams]);

  const applyFilters = useCallback(() => {
    setCurrentPage(1);
    setDebouncedFilters(filters);
    updateUrlParams(1, itemsPerPage, filters);
  }, [filters, itemsPerPage, updateUrlParams]);

  const resetFilters = useCallback(() => {
    const emptyFilters = { name: '', isActive: '' };
    setFilters(emptyFilters);
    setDebouncedFilters(emptyFilters);
    setCurrentPage(1);
    updateUrlParams(1, itemsPerPage, emptyFilters);
  }, [itemsPerPage, updateUrlParams]);

  useEffect(() => {
    const fetchPageList = async () => {
      if (currentPage < 1 || itemsPerPage < 1) return;

      try {
        setFilterLoading(true);
        setError(null);

        const params = {
          start: 0,
          length: 1000,
        };

        const response = await axiosInstance.get(
          '/internal-api/v1/client-web/cms/custom-page/list',
          { params }
        );

        if (response.data?.data) {
          setPageList(response.data.data);
          setTotalItemsAll(response.data.recordsTotal || response.data.data.length || 0);
        } else {
          setPageList([]);
          setTotalItemsAll(0);
        }
      } catch (error) {
        console.error("Error fetching custom pages:", error);
        setError("Failed to load custom pages. Please try again.");
        setPageList([]);
        setTotalItemsAll(0);
      } finally {
        setFilterLoading(false);
      }
    };

    fetchPageList();
  }, []);

  const handleDeletePage = async (pageId) => {
    if (!window.confirm('Are you sure you want to delete this custom page?')) return;

    try {
      await axiosInstance.delete(`/internal-api/v1/client-web/cms/custom-page/${pageId}`);
      
      setPageList(prev => prev.filter(page => page.id !== pageId));
      setTotalItemsAll(prev => Math.max(prev - 1, 0));
    } catch (error) {
      console.error('Error deleting page:', error);
      alert('Failed to delete custom page. Please try again.');
    }
  };

  const handleEditPage = (page) => {
    const params = new URLSearchParams();
    params.set('pageId', page.id);
    params.set('pageName', page.name || page.pageMeta || 'Custom Page');
    navigate(`/CustomPageBlocks?${params.toString()}`);
  };

  const handleEditLandingPage = () => {
    navigate('/LandingPage');
  };

  const handleAddCustomPage = () => {
    navigate('/ViewMore');
  };

  const getStatusBadgeClass = (isActive) => {
    return isActive ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const generatePaginationPages = () => {
    const pages = [];
    const maxVisiblePages = 7;
    const sidePages = 2;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      let startPage = Math.max(2, currentPage - sidePages);
      let endPage = Math.min(totalPages - 1, currentPage + sidePages);

      if (currentPage <= sidePages + 1) endPage = Math.min(totalPages - 1, maxVisiblePages - 2);
      if (currentPage >= totalPages - sidePages) startPage = Math.max(2, totalPages - maxVisiblePages + 2);

      if (startPage > 2) pages.push('...');
      for (let i = startPage; i <= endPage; i++) pages.push(i);
      if (endPage < totalPages - 1) pages.push('...');
      if (totalPages > 1) pages.push(totalPages);
    }
    return pages;
  };

  const paginationPages = generatePaginationPages();

  if (error) {
    return (
      <div className="flex items-center justify-center flex-1">
        <div className="text-red-500">{error}</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Page Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900">
            <FileText className="w-5 h-5 text-brand-blue" />
            Custom Page List
          </h1>
          <div className="mt-1 text-sm text-slate-500">
            Home <span className="mx-1.5 text-slate-400">/</span>
            <span className="font-semibold text-slate-700">Cms</span>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleAddCustomPage}
            className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-white transition-colors rounded-lg shadow-sm bg-brand-blue hover:bg-brand-blue-dark"
          >
            <Plus size={16} /> Add Custom Page
          </button>
          <button className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold transition-colors bg-white border rounded-lg border-slate-300 text-slate-600 hover:bg-slate-50">
            <Download size={16} />
            Export
          </button>
        </div>
      </div>

      {/* Filter Card */}
      <div className="p-6 bg-white border rounded-xl border-slate-200">
        <h2 className="mb-4 text-sm font-bold text-slate-900">Filter Custom Pages</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <label className="block mb-1.5 text-xs font-semibold text-slate-700">Page Name</label>
            <input
              type="text"
              value={filters.name}
              onChange={(e) => handleFilterChange('name', e.target.value)}
              className="w-full px-3 py-2.5 text-sm bg-white border rounded-lg border-slate-300 focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue"
              placeholder="Enter page name"
            />
          </div>
          <div>
            <label className="block mb-1.5 text-xs font-semibold text-slate-700">Status</label>
            <select
              value={filters.isActive}
              onChange={(e) => handleFilterChange('isActive', e.target.value)}
              className="w-full px-3 py-2.5 text-sm bg-white border rounded-lg border-slate-300 focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue"
            >
              <option value="">All Status</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
          <div className="text-xs text-slate-500">
            {Object.values(filters).some(f => f !== '') && <span>Filters applied &mdash; </span>}
            {filters.name && <span className="bg-brand-blue/10 text-brand-blue px-2.5 py-1 rounded-full text-xs font-semibold mr-1">Name: {filters.name}</span>}
            {filters.isActive && <span className="bg-brand-blue/10 text-brand-blue px-2.5 py-1 rounded-full text-xs font-semibold mr-1">Status: {filters.isActive === 'true' ? 'Active' : 'Inactive'}</span>}
          </div>
          <div className="flex gap-2">
            <button
              onClick={applyFilters}
              disabled={filterLoading}
              className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white transition-colors rounded-lg bg-brand-blue hover:bg-brand-blue-dark disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {filterLoading ? (
                <>
                  <div className="w-3 h-3 border-b-2 border-white rounded-full animate-spin"></div>
                  <span>Filtering...</span>
                </>
              ) : (
                <span>Apply Filters</span>
              )}
            </button>
            <button
              onClick={resetFilters}
              disabled={filterLoading}
              className="px-4 py-2 text-sm font-semibold transition-colors bg-white border rounded-lg border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Results Card */}
      <div className="overflow-hidden bg-white border rounded-xl border-slate-200">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">Show</span>
            <select
              value={itemsPerPage}
              onChange={(e) => handlePageSizeChange(parseInt(e.target.value, 10))}
              className="h-8 px-2 py-1 text-xs bg-white border rounded-md border-slate-200 focus:outline-none focus:border-brand-blue"
            >
              {pageSizeOptions.map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
            <span className="text-xs text-slate-500">entries</span>
          </div>
          <div className="text-xs text-slate-500">
            Total: {totalItemsAll} custom pages (+1 landing page)
          </div>
        </div>

        <div className="relative overflow-x-auto">
          {filterLoading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white bg-opacity-75">
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 border-b-2 rounded-full animate-spin border-brand-blue"></div>
                <div className="text-xs text-slate-500">Loading...</div>
              </div>
            </div>
          )}
          <table className="w-full">
            <thead>
              <tr className="border-b bg-slate-50 border-slate-200">
                <th className="px-5 py-3 text-xs font-semibold text-left text-slate-500">No</th>
                <th className="px-3 py-3 text-xs font-semibold text-left text-slate-500">Page Name</th>
                <th className="px-3 py-3 text-xs font-semibold text-left text-slate-500">Created By</th>
                <th className="px-3 py-3 text-xs font-semibold text-left text-slate-500">Created At</th>
                <th className="px-3 py-3 text-xs font-semibold text-left text-slate-500">Status</th>
                <th className="px-3 py-3 text-xs font-semibold text-left text-slate-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {/* Hard-coded Landing Page Row - always first */}
              {shouldShowLandingRow && (
                <tr className="font-medium bg-brand-blue/5">
                <td className="px-5 py-3.5 text-sm text-brand-blue">&mdash;</td>
                <td className="px-3 py-3.5 text-sm font-semibold text-slate-900">Landing Page</td>
                <td className="px-3 py-3.5 text-sm text-slate-600">System</td>
                <td className="px-3 py-3.5 text-sm text-slate-500">&mdash;</td>
                <td className="px-3 py-3.5">
                  <span className="inline-flex items-center h-[22px] px-2.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700">
                    Active
                  </span>
                </td>
                <td className="px-3 py-3.5">
                  <div className="flex gap-1.5">
                    <button
                      onClick={handleEditLandingPage}
                      className="flex items-center justify-center w-7 h-7 transition-colors bg-white border rounded-full border-slate-200 text-slate-500 hover:bg-amber-50 hover:text-amber-600"
                      title="Edit Landing Page"
                    >
                      <Edit2 size={13} />
                    </button>
                  </div>
                </td>
                </tr>
              )}

              {/* Real custom pages */}
              {pagedPages.length > 0 ? (
                pagedPages.map((page, index) => {
                  const displayNumber = (currentPage - 1) * itemsPerPage + index + (shouldShowLandingRow ? 2 : 1);
                  return (
                    <tr
                      key={page.id}
                      className="transition-colors hover:bg-slate-50"
                    >
                      <td className="px-5 py-3.5 text-sm text-slate-700">{displayNumber}</td>
                      <td className="px-3 py-3.5 text-sm font-semibold text-slate-900">{page.name || '-'}</td>
                      <td className="px-3 py-3.5 text-sm text-slate-600">
                        {page.createdBy?.name || page.createdBy || page.createdByUsername || '-'}
                      </td>
                      <td className="px-3 py-3.5 text-sm text-slate-600">{formatDate(page.createdAt)}</td>
                      <td className="px-3 py-3.5">
                        <span className={`inline-flex items-center h-[22px] px-2.5 rounded-full text-xs font-semibold ${getStatusBadgeClass(page.isActive)}`}>
                          {page.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-3 py-3.5">
                        <div className="flex gap-1.5">
                          <button
                            onClick={() => handleEditPage(page)}
                            className="flex items-center justify-center w-7 h-7 transition-colors bg-white border rounded-full border-slate-200 text-slate-500 hover:bg-amber-50 hover:text-amber-600"
                            title="Edit Blocks"
                          >
                            <Edit2 size={13} />
                          </button>
                          <button
                            onClick={() => handleDeletePage(page.id)}
                            className="flex items-center justify-center w-7 h-7 transition-colors bg-white border rounded-full border-slate-200 text-slate-500 hover:bg-red-50 hover:text-red-600"
                            title="Delete"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="6" className="px-3 py-10 text-sm text-center text-slate-400">
                    No custom pages found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalItems > 0 && (
          <div className="flex flex-col items-center justify-between gap-3 p-4 border-t sm:flex-row border-slate-200">
            <div className="text-xs text-slate-500">
              Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} custom pages 
              (plus 1 landing page)
            </div>
            <div className="flex items-center space-x-1">
              <button
                disabled={currentPage === 1 || filterLoading}
                onClick={() => handlePageChange(1)}
                className="flex items-center h-8 px-2 text-xs transition-colors border rounded border-slate-200 text-slate-500 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
                title="First page"
              >
                <span className="hidden sm:inline">First</span>
                <span className="sm:hidden">««</span>
              </button>

              <button
                disabled={currentPage === 1 || filterLoading}
                onClick={() => handlePageChange(currentPage - 1)}
                className="flex items-center h-8 px-2 text-xs transition-colors border rounded border-slate-200 text-slate-500 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
                title="Previous page"
              >
                <span className="hidden sm:inline">Previous</span>
                <span className="sm:hidden">‹</span>
              </button>

              <div className="flex items-center space-x-1">
                {paginationPages.map((page, index) => (
                  page === '...' ? (
                    <span key={`ellipsis-${index}`} className="flex items-center justify-center w-8 h-8 text-xs cursor-default text-slate-400">...</span>
                  ) : (
                    <button
                      key={`page-${page}`}
                      onClick={() => handlePageChange(page)}
                      disabled={filterLoading}
                      className={`h-8 w-8 rounded-md text-xs font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                        page === currentPage
                          ? 'bg-brand-blue text-white border border-brand-blue shadow-sm'
                          : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      {page}
                    </button>
                  )
                ))}
              </div>

              <button
                disabled={currentPage === totalPages || filterLoading}
                onClick={() => handlePageChange(currentPage + 1)}
                className="flex items-center h-8 px-2 text-xs transition-colors border rounded border-slate-200 text-slate-500 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
                title="Next page"
              >
                <span className="hidden sm:inline">Next</span>
                <span className="sm:hidden">›</span>
              </button>

              <button
                disabled={currentPage === totalPages || filterLoading}
                onClick={() => handlePageChange(totalPages)}
                className="flex items-center h-8 px-2 text-xs transition-colors border rounded border-slate-200 text-slate-500 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
                title="Last page"
              >
                <span className="hidden sm:inline">Last</span>
                <span className="sm:hidden">»»</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomPageList;
